<template>
  <div>
    <!-- Page header -->
    <CCard class="mb-4">
      <CCardBody class="p-4">
        <CRow class="align-items-center">
          <CCol md="7">
            <h1 class="h4 mb-1 font-weight-bold">Users</h1>
            <p class="text-muted mb-0">Everyone who can sign in to Niagaku</p>
          </CCol>
          <CCol md="5" class="d-flex justify-content-md-end mt-3 mt-md-0">
            <CButton
              color="light"
              class="mr-2"
              aria-label="Refresh"
              :disabled="loading"
              @click="refreshTable"
            >
              <CIcon name="cil-reload" />
            </CButton>
            <CButton color="primary" @click="addNew">
              <CIcon name="cil-plus" /> Add user
            </CButton>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <CCard>
      <CCardBody>
        <div v-if="loading && !items.length" class="text-center p-5">
          <CSpinner color="primary" />
        </div>
        <div v-else-if="loadError" class="text-center p-5">
          <CIcon name="cil-warning" size="xl" class="text-danger mb-3" />
          <h2 class="h6 font-weight-bold">Couldn't load users</h2>
          <p class="text-muted">Check your connection and try again.</p>
          <CButton color="primary" @click="refreshTable">Retry</CButton>
        </div>
        <div v-else-if="!items.length" class="text-center p-5">
          <CIcon name="cil-people" size="xl" class="text-muted mb-3" />
          <h2 class="h6 font-weight-bold">No users yet</h2>
          <p class="text-muted">Users appear here when they register or you add them.</p>
          <CButton color="primary" @click="addNew">Add user</CButton>
        </div>
        <template v-else>
          <!-- Desktop table -->
          <div class="d-none d-lg-block">
            <CDataTable
              :items="items"
              :fields="fields"
              column-filter
              items-per-page-select
              :items-per-page="25"
              hover
              sorter
              pagination
              table-filter
              cleaner
              :loading="loading"
            >
              <template #actions="{ item }">
                <td class="text-nowrap">
                  <CButton
                    color="light"
                    size="sm"
                    class="mr-1"
                    :aria-label="`Edit ${item.fullName}`"
                    @click="onEdit(item)"
                  >
                    <CIcon name="cil-pencil" />
                  </CButton>
                  <CButton
                    color="light"
                    size="sm"
                    :aria-label="`Delete ${item.fullName}`"
                    @click="showDeleteConfirmation(item)"
                  >
                    <CIcon name="cil-trash" class="text-danger" />
                  </CButton>
                </td>
              </template>
            </CDataTable>
          </div>

          <!-- Mobile card list -->
          <div class="d-lg-none">
            <CInput
              v-model="search"
              placeholder="Search name or email"
              aria-label="Search name or email"
            />
            <div v-if="!filteredItems.length" class="text-center p-4">
              <p class="text-muted mb-2">No users match your search</p>
              <CButton color="light" @click="search = ''">Clear search</CButton>
            </div>
            <div
              v-for="item in filteredItems"
              :key="item.id"
              class="border rounded p-3 mb-2"
            >
              <div class="font-weight-bold">{{ item.fullName }}</div>
              <div class="small text-muted">{{ item.email }}</div>
              <div class="mt-2">
                <CButton color="light" size="sm" class="mr-2" @click="onEdit(item)">
                  <CIcon name="cil-pencil" /> Edit
                </CButton>
                <CButton color="light" size="sm" @click="showDeleteConfirmation(item)">
                  <CIcon name="cil-trash" class="text-danger" /> Delete
                </CButton>
              </div>
            </div>
          </div>
        </template>
      </CCardBody>
    </CCard>

    <CModal
      :title="`Delete ${itemToDelete.fullName || 'this user'}?`"
      :show.sync="warningModal"
      :close-on-backdrop="!deleting"
    >
      {{ itemToDelete.fullName || "This user" }}
      <span v-if="itemToDelete.email">({{ itemToDelete.email }})</span>
      will be removed and can no longer sign in. This can't be undone.
      <template #footer>
        <CButton color="light" :disabled="deleting" @click="warningModal = false">
          Keep it
        </CButton>
        <CButton color="danger" :disabled="deleting" @click="doDelete">
          Delete user
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import ProfileApi from "../../lib/profileApi";

const fields = [
  { key: "fullName", label: "Full name", _style: "min-width:200px;" },
  { key: "email", label: "Email", _style: "min-width:200px;" },
  { key: "actions", label: "", _style: "width:1%", sorter: false, filter: false },
];

export default {
  name: "UserList",
  data() {
    return {
      loading: true,
      loadError: false,
      deleting: false,
      search: "",
      items: [],
      fields,
      api: new ProfileApi(),
      warningModal: false,
      itemToDelete: {},
    };
  },
  computed: {
    filteredItems() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.items;
      return this.items.filter(
        (i) =>
          (i.fullName || "").toLowerCase().includes(q) ||
          (i.email || "").toLowerCase().includes(q)
      );
    },
  },
  mounted() {
    this.refreshTable();
  },
  methods: {
    refreshTable() {
      var self = this;
      self.loading = true;
      self.loadError = false;
      self.api
        .getProfileList()
        .then((response) => {
          self.items = response.result || [];
        })
        .catch((error) => {
          console.error(error);
          self.loadError = true;
        })
        .finally(() => {
          self.loading = false;
        });
    },
    onEdit(item) {
      this.$router.push({ path: `/admins/user/${item.id}` });
    },
    showDeleteConfirmation(item) {
      this.itemToDelete = item;
      this.warningModal = true;
    },
    doDelete() {
      var self = this;
      self.deleting = true;
      self.api
        .deleteProfile(self.itemToDelete.id)
        .then(() => {
          self.$toast.success("User deleted");
          self.warningModal = false;
          self.itemToDelete = {};
          self.refreshTable();
        })
        .catch(({ data }) => {
          self.$toast.error("Couldn't delete the user. " + helper.getErrorMessage(data));
        })
        .finally(() => {
          self.deleting = false;
        });
    },
    addNew() {
      this.$router.push({ path: "/admins/user" });
    },
  },
};
</script>
